import {
  findSiblingNodeId,
  findTreemapNodeById,
  type LayoutResult,
} from './layout-treemap';
import type { ParsedTreemap } from './treemap-types';
import type { TreemapViewportRect } from './treemap-view-model';

interface BaseFocusInput {
  parsed: ParsedTreemap | null;
  layout: LayoutResult | null;
  hoveredNodeId: string | null;
  rectById: Map<string, TreemapViewportRect>;
}

interface SiblingFocusInput extends BaseFocusInput {
  parentMap: Map<string, string>;
  offset: 1 | -1;
}

interface ParentFocusInput extends BaseFocusInput {
  parentMap: Map<string, string>;
}

function getCurrentFocusId(input: BaseFocusInput): string | null {
  return input.hoveredNodeId ?? input.layout?.nodes[0]?.id ?? null;
}

function getVisibleNodeId(input: {
  candidateId: string | null;
  rectById: Map<string, TreemapViewportRect>;
}): string | null {
  return input.candidateId && input.rectById.has(input.candidateId) ? input.candidateId : null;
}

export function getSiblingTreemapFocusId(input: SiblingFocusInput): string | null {
  if (!input.parsed) {
    return null;
  }

  const currentId = getCurrentFocusId(input);
  if (!currentId) {
    return null;
  }

  return getVisibleNodeId({
    candidateId: findSiblingNodeId({
      root: input.parsed.root,
      nodeId: currentId,
      parentMap: input.parentMap,
      offset: input.offset,
    }),
    rectById: input.rectById,
  });
}

export function getParentTreemapFocusId(input: ParentFocusInput): string | null {
  if (!input.parsed) {
    return null;
  }

  const currentId = getCurrentFocusId(input);
  const parentId = currentId ? input.parentMap.get(currentId) ?? null : null;
  const renderedParentId = parentId !== input.parsed.root.id ? parentId : null;

  return getVisibleNodeId({
    candidateId: renderedParentId,
    rectById: input.rectById,
  });
}

export function getFirstChildTreemapFocusId(input: BaseFocusInput): string | null {
  if (!input.parsed) {
    return null;
  }

  const currentId = getCurrentFocusId(input);
  if (!currentId) {
    return null;
  }

  const node = findTreemapNodeById({ root: input.parsed.root, nodeId: currentId });
  const firstChildId = node?.children.find((child) => input.rectById.has(child.id))?.id ?? null;

  return getVisibleNodeId({
    candidateId: firstChildId,
    rectById: input.rectById,
  });
}
